import { useEffect, useState } from "react";
import { Plus, Trash2, Lock, Unlock, Trophy, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const DEFAULT_TIMES = "10:00-10:45,11:00-11:45,12:00-12:45,14:00-14:45,15:00-15:45,16:00-16:45,17:00-17:45";

export default function SlotsAdmin() {
  const [games, setGames] = useState([]);
  const [locs, setLocs] = useState([]);
  const [gameId, setGameId] = useState("");
  const [locId, setLocId] = useState("");
  const [slots, setSlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState({ from: "", to: "", times: DEFAULT_TIMES, price: 499, min_players: 3, max_players: 5 });
  const [sponsors, setSponsors] = useState([]);
  const [sp, setSp] = useState(null);
  const [spForm, setSpForm] = useState({ is_challenge_slot: false, sponsor: "", sponsor_logo: "", prize: "", price_override: "" });

  useEffect(() => {
    api.get("/admin/games").then((r) => { setGames(r.data); if (r.data[0]) setGameId(r.data[0].id); });
    api.get("/admin/locations").then((r) => { setLocs(r.data); if (r.data[0]) setLocId(r.data[0].id); });
    api.get("/admin/sponsors").then((r) => setSponsors(r.data));
  }, []);

  const load = () => { if (gameId && locId) api.get("/admin/slots", { params: { game_id: gameId, location_id: locId } }).then((r) => setSlots(r.data)); };
  useEffect(() => { load(); }, [gameId, locId]);
  useEffect(() => { const g = games.find((x) => x.id === gameId); if (g && g.base_price) setBulk((b) => ({ ...b, price: g.base_price })); }, [gameId, games]);

  const genDates = () => {
    const out = [];
    const start = new Date(bulk.from), end = new Date(bulk.to);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d).toISOString().slice(0, 10));
    return out;
  };

  const createBulk = async () => {
    if (!bulk.from || !bulk.to) return toast.error("Pick a date range");
    try {
      const { data } = await api.post("/admin/slots/bulk", {
        game_id: gameId, location_id: locId, dates: genDates(),
        times: bulk.times.split(",").map((t) => t.trim()).filter(Boolean),
        price: +bulk.price, min_players: +bulk.min_players, max_players: +bulk.max_players,
      });
      toast.success(`${data.created} slots created`); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (s) => {
    const status = s.status === "blocked" ? "available" : "blocked";
    await api.put(`/admin/slots/${s.id}`, { status }); load();
  };
  const del = async (id) => { await api.delete(`/admin/slots/${id}`); load(); };

  const openSponsor = (s) => { setSp(s); setSpForm({ is_challenge_slot: !!s.is_challenge_slot, sponsor: s.sponsor || "", sponsor_logo: s.sponsor_logo || "", prize: s.prize || "", price_override: s.price_override ?? "" }); };
  const saveSponsor = async () => {
    try { await api.put(`/admin/slots/${sp.id}`, spForm); toast.success("Slot updated"); setSp(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const byDate = slots.reduce((a, s) => { (a[s.date] = a[s.date] || []).push(s); return a; }, {});

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display font-extrabold text-3xl">Slots</h1><p className="text-slate-400">Schedule sessions per game & location.</p></div>
        <Button data-testid="bulk-slots-btn" onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-500"><Plus className="h-4 w-4 mr-1" /> Generate slots</Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={gameId} onValueChange={setGameId}><SelectTrigger data-testid="slot-game-filter" className="w-48 bg-[#131a29] border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select>
        <Select value={locId} onValueChange={setLocId}><SelectTrigger data-testid="slot-loc-filter" className="w-48 bg-[#131a29] border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="space-y-5">
        {Object.entries(byDate).map(([date, list]) => (
          <div key={date}>
            <div className="text-sm font-mono text-slate-400 mb-2">{new Date(date).toDateString()}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {list.map((s) => (
                <div key={s.id} data-testid={`admin-slot-${s.id}`}
                  className={`rounded-lg border p-2.5 text-sm ${s.status === "booked" ? "border-slate-600 bg-slate-800/40 opacity-70" : s.status === "blocked" ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{s.start_time}</span>
                    {s.is_challenge_slot && <Trophy className="h-3 w-3 text-amber-400" />}
                  </div>
                  <div className="text-xs text-slate-500">{s.price_override ? `₹${s.price_override}` : "auto"} · {s.status}</div>
                  {s.is_challenge_slot && <div className="text-[10px] text-amber-400 truncate flex items-center gap-1">{s.sponsor_logo && <img src={s.sponsor_logo} alt="" className="h-3 w-auto object-contain bg-white/90 rounded-sm" />}🏆 {s.sponsor || "Sponsored"}{s.prize ? ` · ${s.prize}` : ""}</div>}
                  <div className="flex gap-1 mt-1">
                    {s.status !== "booked" && (
                      <>
                        <button onClick={() => toggle(s)} className="p-1 hover:text-white text-slate-400" data-testid={`toggle-slot-${s.id}`}>{s.status === "blocked" ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}</button>
                        <button onClick={() => openSponsor(s)} className="p-1 text-amber-400 hover:text-amber-300" data-testid={`sponsor-slot-${s.id}`}><Gift className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(s.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {slots.length === 0 && <p className="text-slate-500">No slots yet. Generate some above.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131a29] border-white/10">
          <DialogHeader><DialogTitle>Generate slots</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">From</label><Input type="date" value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: e.target.value })} data-testid="bulk-from" className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400">To</label><Input type="date" value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: e.target.value })} data-testid="bulk-to" className="bg-slate-950/50 border-white/10" /></div>
            </div>
            <div><label className="text-xs text-slate-400">Times (comma separated, HH:MM-HH:MM)</label><Input value={bulk.times} onChange={(e) => setBulk({ ...bulk, times: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-slate-400">Price</label><Input type="number" value={bulk.price} onChange={(e) => setBulk({ ...bulk, price: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400">Min</label><Input type="number" value={bulk.min_players} onChange={(e) => setBulk({ ...bulk, min_players: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400">Max</label><Input type="number" value={bulk.max_players} onChange={(e) => setBulk({ ...bulk, max_players: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={createBulk} data-testid="save-bulk" className="bg-violet-600 hover:bg-violet-500">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sp} onOpenChange={() => setSp(null)}>
        <DialogContent className="bg-[#131a29] border-white/10">
          <DialogHeader><DialogTitle>Slot settings · {sp?.start_time}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Price override per person (₹) — leave blank to use the game's group pricing</label>
              <Input type="number" placeholder="Blank = auto (group pricing)" value={spForm.price_override} onChange={(e) => setSpForm({ ...spForm, price_override: e.target.value === "" ? "" : +e.target.value })} data-testid="sp-price-per-person" className="bg-slate-950/50 border-white/10" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={spForm.is_challenge_slot} onCheckedChange={(v) => setSpForm({ ...spForm, is_challenge_slot: !!v })} data-testid="sp-toggle" />
              Mark as sponsored challenge slot
            </label>
            {sponsors.length === 0 ? (
              <p className="text-xs text-amber-300/90 border border-amber-500/30 rounded-lg p-2" data-testid="sp-no-sponsors">No sponsors yet. Add a sponsor (with logo) in the <b>Marketing</b> tab first, then assign them here.</p>
            ) : (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Sponsor</label>
                <Select value={spForm.sponsor || undefined} onValueChange={(name) => { const found = sponsors.find((x) => x.name === name); setSpForm({ ...spForm, sponsor: name, sponsor_logo: found?.logo_url || "" }); }}>
                  <SelectTrigger data-testid="sp-sponsor-select" className="bg-slate-950/50 border-white/10"><SelectValue placeholder="Choose a sponsor" /></SelectTrigger>
                  <SelectContent>{sponsors.map((x) => <SelectItem key={x.id} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {spForm.sponsor_logo && <div className="flex items-center gap-2 text-xs text-slate-400"><span className="bg-white/90 rounded px-1.5 py-1"><img src={spForm.sponsor_logo} alt="" className="h-6 w-auto object-contain" /></span> This logo will show on the booking slot</div>}
            <Input placeholder="Prize (e.g. ₹5000 voucher)" value={spForm.prize} onChange={(e) => setSpForm({ ...spForm, prize: e.target.value })} data-testid="sp-prize" className="bg-slate-950/50 border-white/10" />
          </div>
          <DialogFooter><Button onClick={saveSponsor} data-testid="save-sponsor-slot" className="bg-violet-600 hover:bg-violet-500">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
